import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom"
import {
	Heart, Trash2, Plus, Star, Edit3, Check, X,
	Globe, Lock, ChevronDown, ChevronUp, Package
} from "lucide-react"
import { useAuth } from "../contexts/AuthContext"
import { fetchProfileDirect, collectionAPI, wishlistAPI } from '../lib/api'
import EditItemModal from "./EditItemModal"
import CollectionSelectorModal from "./CollectionSelectorModal"
import PageMeta from "./PageMeta"
import PriceDisplay from "./PriceDisplay"
import GameCardArt from "./GameCardArt"
import SealedArt from "./SealedArt"
import { gameLabel, gameOfId, isProductCardId } from "../lib/games"
import { itemHref, itemKind } from "../lib/collectionItems"

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const apiUrl = process.env.REACT_APP_API_URL || ""

/**
 * Fetch card info from the card API for items that lack card_data.
 * Returns a Map<card_id, {name, image_small, ...}>.
 */
const fetchMissingCardData = async (cardIds) => {
	const results = new Map()
	// Batch fetch - up to 20 concurrently
	const batches = []
	for (let i = 0; i < cardIds.length; i += 20) {
		batches.push(cardIds.slice(i, i + 20))
	}
	for (const batch of batches) {
		const promises = batch.map(async (id) => {
			try {
				// WO-35: other games' cards and sealed products are served by /api/products, not /api/card
				if (!isProductCardId(id) && !String(id).includes(".")) {
					const res = await fetch(`${apiUrl}/api/card/${encodeURIComponent(id)}`)
					if (res.ok) {
						const data = await res.json()
						results.set(id, {
							name: data.name,
							image_small: data.image_small,
							image_large: data.image_large,
							rarity: data.rarity,
							set_id: data.set_id,
							number: data.number,
						})
						return
					}
				}
				const res = await fetch(`${apiUrl}/api/products/${encodeURIComponent(id)}`)
				if (res.ok) {
					const data = await res.json()
					results.set(id, {
						name: data.name,
						image_small: data.image_small,
						image_large: data.image_large,
						rarity: Array.isArray(data.rarities) ? data.rarities[0] : data.rarity,
						set_id: data.set_id,
						set_name: data.set_name,
						number: data.number,
						game_id: data.game_id,
						product_type: data.product_type,
					})
				}
			} catch { /* ignore */ }
		})
		await Promise.all(promises)
	}
	return results
}

const conditionColors = {
	"Mint": "text-emerald-400",
	"Near Mint": "text-green-400",
	"Lightly Played": "text-yellow-400",
	"Moderately Played": "text-orange-400",
	"Heavily Played": "text-red-400",
	"Damaged": "text-red-500",
}

/* ------------------------------------------------------------------ */
/*  ProfilePage                                                        */
/* ------------------------------------------------------------------ */

function ProfilePage() {
	const { username } = useParams()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { user, profile: currentUserProfile } = useAuth()
	const searchSuffix = searchParams.toString()

	const tabFromUrl = searchParams.get("tab")
	const [activeTab, setActiveTab] = useState(tabFromUrl === "wishlist" ? "wishlist" : "collections")
	const [profileData, setProfileData] = useState(null)
	const [collections, setCollections] = useState([])
	const [wishlist, setWishlist] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const displayName = profileData?.display_name || profileData?.username || username
	const title = displayName ? `${displayName} | Lilycove` : "Profile | Lilycove"

	// Card data cache for items missing snapshots
	const [cardDataCache, setCardDataCache] = useState(new Map())

	// Collection CRUD state
	const [showCreateCollection, setShowCreateCollection] = useState(false)
	const [newColName, setNewColName] = useState("")
	const [newColDesc, setNewColDesc] = useState("")
	const [newColPublic, setNewColPublic] = useState(false)
	const [creatingCollection, setCreatingCollection] = useState(false)

	// Edit collection inline state
	const [editingCollectionId, setEditingCollectionId] = useState(null)
	const [editColName, setEditColName] = useState("")
	const [editColDesc, setEditColDesc] = useState("")
	const [editColPublic, setEditColPublic] = useState(false)
	const [savingCollection, setSavingCollection] = useState(false)

	// Collapsed collections
	const [collapsedCollections, setCollapsedCollections] = useState(new Set())

	// Edit item modal state
	const [editingItem, setEditingItem] = useState(null)
	const [editingItemMode, setEditingItemMode] = useState("collection")
	const [editingItemCollectionId, setEditingItemCollectionId] = useState(null)

	// Collection selector for wishlist → collection move
	const [moveCardId, setMoveCardId] = useState(null)

	const isOwnProfile =
		(currentUserProfile?.username || "").toLowerCase() === (username || "").toLowerCase()

	/* ---- Data loading ---- */

	const loadProfileData = useCallback(async () => {
		try {
			setLoading(true)
			setError(null)

			const profile = await fetchProfileDirect(null, username)
			if (!profile) { setError("Profile not found"); return }

			const canonicalUsername = profile?.username
			if (canonicalUsername && canonicalUsername !== username) {
				const nextUrl = `/profile/${canonicalUsername}${searchSuffix ? `?${searchSuffix}` : ""}`
				navigate(nextUrl, { replace: true })
				return
			}

			setProfileData(profile)

			if (isOwnProfile) {
				// Own profile: use authenticated endpoints
				const [collectionsResult, wishlistResult] = await Promise.all([
					collectionAPI.getUserCollections(profile.id),
					wishlistAPI.getUserWishlist(profile.id),
				])

				let allItems = []

				if (collectionsResult.success && collectionsResult.data) {
					const collectionsWithItems = await Promise.all(
						collectionsResult.data.map(async (collection) => {
							const itemsResult = await collectionAPI.getCollectionItems(collection.id)
							const items = itemsResult.success ? (itemsResult.data.items || []) : []
							allItems.push(...items)
							return { ...collection, items }
						})
					)
					setCollections(collectionsWithItems)
				}

				if (wishlistResult.success && wishlistResult.data) {
					setWishlist(wishlistResult.data)
					allItems.push(...wishlistResult.data)
				}

				// Fetch card data for items without snapshots
				const missingIds = [
					...new Set(
						allItems
							.filter((it) => !it.card_data)
							.map((it) => it.card_id)
					),
				]
				if (missingIds.length > 0) {
					const fetched = await fetchMissingCardData(missingIds)
					setCardDataCache((prev) => {
						const merged = new Map(prev)
						fetched.forEach((v, k) => merged.set(k, v))
						return merged
					})
				}
			} else {
				// Other user's profile: use public endpoint
				const publicResult = await collectionAPI.getPublicProfile(profile.id)
				if (publicResult.success && publicResult.data) {
					setCollections(publicResult.data.collections || [])
					setWishlist(publicResult.data.wishlist || [])

					const allItems = [
						...(publicResult.data.collections || []).flatMap(c => c.items || []),
						...(publicResult.data.wishlist || []),
					]
					const missingIds = [...new Set(allItems.filter(it => !it.card_data).map(it => it.card_id))]
					if (missingIds.length > 0) {
						const fetched = await fetchMissingCardData(missingIds)
						setCardDataCache((prev) => {
							const merged = new Map(prev)
							fetched.forEach((v, k) => merged.set(k, v))
							return merged
						})
					}
				}
			}
		} catch (err) {
			console.error("Error loading profile:", err)
			setError("Failed to load profile")
		} finally {
			setLoading(false)
		}
	}, [username, isOwnProfile, navigate, searchSuffix])

	useEffect(() => { loadProfileData() }, [loadProfileData])

	/* ---- Collection CRUD ---- */

	const handleCreateCollection = async () => {
		if (!newColName.trim()) return
		setCreatingCollection(true)
		try {
			const result = await collectionAPI.createCollection(user.id, {
				name: newColName.trim(),
				description: newColDesc.trim() || null,
				is_public: newColPublic,
			})
			if (result.success) {
				setCollections((prev) => [...prev, { ...result.data, items: [] }])
				setNewColName("")
				setNewColDesc("")
				setNewColPublic(false)
				setShowCreateCollection(false)
			}
		} catch (err) {
			console.error("Error creating collection:", err)
		} finally {
			setCreatingCollection(false)
		}
	}

	const startEditCollection = (col) => {
		setEditingCollectionId(col.id)
		setEditColName(col.name)
		setEditColDesc(col.description || "")
		setEditColPublic(col.is_public)
	}

	const saveEditCollection = async (colId) => {
		setSavingCollection(true)
		try {
			const result = await collectionAPI.updateCollection(colId, {
				name: editColName.trim(),
				description: editColDesc.trim() || null,
				is_public: editColPublic,
			})
			if (result.success) {
				setCollections((prev) =>
					prev.map((c) =>
						c.id === colId ? { ...c, name: editColName.trim(), description: editColDesc.trim() || null, is_public: editColPublic } : c
					)
				)
				setEditingCollectionId(null)
			}
		} catch (err) {
			console.error("Error updating collection:", err)
		} finally {
			setSavingCollection(false)
		}
	}

	const handleDeleteCollection = async (colId) => {
		const col = collections.find((c) => c.id === colId)
		if (col?.is_default) {
			alert("Cannot delete your default collection.")
			return
		}
		if (!window.confirm(`Delete "${col?.name}"? All items will be removed.`)) return
		try {
			await collectionAPI.deleteCollection(colId)
			setCollections((prev) => prev.filter((c) => c.id !== colId))
		} catch (err) {
			console.error("Error deleting collection:", err)
		}
	}

	const toggleCollapseCollection = (colId) => {
		setCollapsedCollections((prev) => {
			const next = new Set(prev)
			next.has(colId) ? next.delete(colId) : next.add(colId)
			return next
		})
	}

	/* ---- Item editing ---- */

	const openEditItem = (item, mode, collectionId = null) => {
		setEditingItem(item)
		setEditingItemMode(mode)
		setEditingItemCollectionId(collectionId)
	}

	const handleSaveItem = async (updates) => {
		if (editingItemMode === "collection") {
			await collectionAPI.updateCollectionItem(
				editingItemCollectionId,
				editingItem.card_id,
				updates
			)
			// Update local state
			setCollections((prev) =>
				prev.map((c) =>
					c.id === editingItemCollectionId
						? {
								...c,
								items: c.items.map((it) =>
									it.card_id === editingItem.card_id ? { ...it, ...updates } : it
								),
						  }
						: c
				)
			)
		} else {
			await wishlistAPI.updateWishlistItem(editingItem.card_id, updates)
			setWishlist((prev) =>
				prev.map((it) =>
					it.card_id === editingItem.card_id ? { ...it, ...updates } : it
				)
			)
		}
	}

	const handleDeleteItem = async () => {
		if (editingItemMode === "collection") {
			await collectionAPI.removeFromCollection(
				editingItemCollectionId,
				editingItem.card_id
			)
			setCollections((prev) =>
				prev.map((c) =>
					c.id === editingItemCollectionId
						? { ...c, items: c.items.filter((it) => it.card_id !== editingItem.card_id) }
						: c
				)
			)
		} else {
			await wishlistAPI.removeFromWishlist(editingItem.card_id)
			setWishlist((prev) => prev.filter((it) => it.card_id !== editingItem.card_id))
		}
	}

	const handleMoveToCollection = async (cardId) => {
		// Show collection selector instead of quick-adding to default
		setMoveCardId(cardId)
	}

	const handleMoveCollectionSelected = async (collectionId) => {
		const cardId = moveCardId
		setMoveCardId(null)
		try {
			await collectionAPI.addToCollection(collectionId, cardId, {
				quantity: 1,
				condition: "Near Mint"
			})
			await wishlistAPI.removeFromWishlist(cardId)
			loadProfileData()
		} catch (err) {
			console.error("Error moving to collection:", err)
		}
	}

	/* ---- Stats ---- */

	const totalCards = collections.reduce((t, c) => t + (c.items?.length || 0), 0)
	const totalValue = collections.reduce((t, c) => {
		return t + (c.items?.reduce((s, it) => s + (it.purchase_price || 0) * (it.quantity || 1), 0) || 0)
	}, 0)

	/* ---- Resolve card data (from snapshot or cache) ---- */

	const resolveCard = (item) => {
		if (item.card_data) return item.card_data
		return cardDataCache.get(item.card_id) || null
	}

	/* ================================================================ */
	/*  Sub-components                                                   */
	/* ================================================================ */

	const CardTile = ({ item, type, collectionId }) => {
		const card = resolveCard(item)
		const image = card?.image_small || card?.image_large || null
		const name = card?.name || item.card_id
		// WO-35: other games and sealed products: their own page, drawn art when there is no image, a game badge
		const kind = itemKind(item.card_id, card)
		const gameId = card?.game_id || gameOfId(item.card_id) || "pokemon"
		const href = itemHref(item.card_id, card)

		return (
			<div
				className={`group relative bg-[#12121a] rounded-lg overflow-hidden border border-purple/10
					hover:border-purple/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple/10
					${isOwnProfile ? "cursor-pointer" : ""}`}
				onClick={() => {
					if (isOwnProfile) {
						openEditItem(item, type, collectionId)
					}
				}}
			>
				{/* Card Image */}
				<div className="relative aspect-[2.5/3.5] overflow-hidden bg-dark">
					{image ? (
						<img
							src={image}
							alt={name}
							className="w-full h-full object-contain p-1 transition-transform duration-500 group-hover:scale-105"
							loading="lazy"
							onError={(e) => {
								e.target.style.display = "none"
								e.target.nextSibling.style.display = "flex"
							}}
						/>
					) : null}
					<div
						className={`w-full h-full bg-gradient-to-br from-purple/20 to-dark items-center justify-center ${image ? "hidden" : "flex"}`}
					>
						{kind === "sealed" ? (
							<SealedArt productType={card?.product_type} alt={name} className="w-full h-full" />
						) : kind === "game-card" ? (
							<GameCardArt gameId={gameId} name={name} number={card?.number} subtitle={card?.set_name} compact />
						) : (
							<span className="text-cream/40 text-xs text-center px-2 font-mono">{item.card_id}</span>
						)}
					</div>
					{gameId !== "pokemon" && (
						<div className="absolute bottom-1.5 left-1.5 bg-dark/80 border border-purple/40 text-cream/80 text-[9px] px-1.5 py-0.5 rounded-md" data-testid="item-game-badge">
							{gameLabel(gameId)}
						</div>
					)}

					{/* Hover overlay — quick actions */}
					{isOwnProfile && (
						<div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
							<span className="text-cream/80 text-xs font-medium bg-purple/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
								{type === "collection" ? "Edit Card" : "Edit Item"}
							</span>
						</div>
					)}

					{/* Quantity badge */}
					{type === "collection" && item.quantity > 1 && (
						<div className="absolute top-1.5 left-1.5 bg-purple/90 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md">
							x{item.quantity}
						</div>
					)}

					{/* Priority badge for wishlist */}
					{type === "wishlist" && item.target_price != null && (
						<div className="absolute top-1.5 left-1.5 bg-green-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md">
							<PriceDisplay amountCad={item.target_price} className="text-white" />
						</div>
					)}

					{/* Rarity badge */}
					{card?.rarity && (card.rarity.toLowerCase().includes("rare") || card.rarity.toLowerCase().includes("holo")) && (
						<div className="absolute top-1.5 right-1.5 bg-gray-900/70 backdrop-blur-sm border border-yellow-500/40 text-yellow-300 text-[9px] font-light px-1.5 py-0.5 rounded-md tracking-wide">
							{card.rarity.length > 12 ? card.rarity.slice(0, 12) + "…" : card.rarity}
						</div>
					)}
				</div>

				{/* Card Info */}
				<div className="p-2.5 space-y-1">
					<Link
						to={href}
						onClick={(e) => e.stopPropagation()}
						className="block"
					>
						<h3 className="font-medium text-cream text-xs leading-tight truncate hover:text-purple transition-colors">
							{name}
						</h3>
					</Link>
					<p className="text-[10px] text-cream/40 font-mono">{item.card_id}</p>

					{type === "collection" && (
						<div className="flex items-center justify-between pt-0.5">
							{item.condition && (
								<span className={`text-[10px] font-medium ${conditionColors[item.condition] || "text-cream/50"}`}>
									{item.condition}
								</span>
							)}
							{item.purchase_price != null && item.purchase_price > 0 && (
								<span className="text-[10px] font-semibold text-green-400">
									<PriceDisplay amountCad={item.purchase_price} />
								</span>
							)}
						</div>
					)}

					{type === "wishlist" && (
						<div className="flex items-center justify-between pt-0.5">
							{item.target_price != null ? (
								<span className="text-[10px] font-semibold text-green-400">
									Target: <PriceDisplay amountCad={item.target_price} />
								</span>
							) : (
								<span className="text-[10px] text-cream/30">No target price</span>
							)}
							{isOwnProfile && (
								<button
									onClick={(e) => { e.stopPropagation(); handleMoveToCollection(item.card_id) }}
									className="text-[10px] text-purple hover:text-purple/80 font-medium transition-colors"
									title="Move to collection"
								>
									+ Collect
								</button>
							)}
						</div>
					)}
				</div>
			</div>
		)
	}

	const EmptyState = ({ type }) => (
		<div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
			<div className="w-24 h-24 rounded-full bg-purple/10 flex items-center justify-center mb-5">
				{type === "collections" ? (
					<Package className="w-10 h-10 text-purple/60" />
				) : (
					<Heart className="w-10 h-10 text-purple/60" />
				)}
			</div>
			<h3 className="text-lg font-semibold text-cream mb-1.5">
				{type === "collections" ? "No cards yet" : "No wishlist items yet"}
			</h3>
			<p className="text-cream/50 text-center max-w-sm text-sm">
				{type === "collections"
					? isOwnProfile
						? "Browse cards and add them to your collection!"
						: "This user hasn't added any cards yet."
					: isOwnProfile
					? "Browse cards and add ones you're looking for!"
					: "This user hasn't added any wishlist items yet."}
			</p>
		</div>
	)

	/* ================================================================ */
	/*  Render                                                           */
	/* ================================================================ */

	if (loading) {
		return (
			<div className="min-h-screen bg-dark flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto mb-4" />
					<p className="text-cream/70 text-sm">Loading profile…</p>
				</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="min-h-screen bg-dark flex items-center justify-center">
				<div className="text-center">
					<h1 className="text-2xl font-bold text-cream mb-2">Profile Not Found</h1>
					<p className="text-cream/60 mb-4">{error}</p>
					<button onClick={() => navigate("/")}
						className="bg-purple hover:bg-purple/90 text-cream px-4 py-2 rounded-lg transition-colors text-sm">
						Go Home
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-dark">
			<PageMeta title={title} description={`View ${displayName || "profile"} on Lilycove.`} />
			<div className="max-w-6xl mx-auto px-4 py-8">

				{/* ========================= Profile Header ========================= */}
				<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-10">
					<div className="relative shrink-0">
						<div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple to-purple/70 p-0.5">
							<img
								src={profileData?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData?.username || "U")}&background=1a1a2e&color=6A66A3&size=128`}
								alt="Profile"
								className="w-full h-full rounded-full object-cover bg-dark"
							/>
						</div>
					</div>

					<div className="flex-1 text-center md:text-left">
						<h1 className="text-3xl font-bold text-cream mb-1">
							{profileData?.display_name || profileData?.username}
						</h1>
						<p className="text-cream/50 mb-4 flex items-center justify-center md:justify-start gap-2 text-sm">
							<span>@{profileData?.username}</span>
							{(profileData?.city_locality || profileData?.city) && (
								<>
									<span className="text-cream/30">•</span>
									<span>
										{profileData?.city_locality || profileData?.city}
										{profileData?.region_name && `, ${profileData.region_name}`}
									</span>
								</>
							)}
						</p>

						{/* Stats */}
						<div className="flex flex-wrap gap-5 mb-5 justify-center md:justify-start">
							{[
								{ value: collections.length, label: "Collections" },
								{ value: totalCards, label: "Items" },
								{ value: wishlist.length, label: "Wishlist" },
								// WO-35: this is the sum of purchase prices, not a market value (was labelled "Value")
								{ priceCad: totalValue, label: "Paid" },
							].map(({ value, priceCad, label }) => (
								<div key={label} className="text-center min-w-[60px]">
									<div className="text-xl font-bold text-purple">
										{priceCad != null ? <PriceDisplay amountCad={priceCad} /> : value}
									</div>
									<div className="text-xs text-cream/50">{label}</div>
								</div>
							))}
						</div>

						{isOwnProfile && (
							<button onClick={() => navigate("/edit-profile")}
								className="bg-purple hover:bg-purple/90 text-cream font-medium px-5 py-2 rounded-lg
									transition-all duration-300 hover:shadow-lg hover:shadow-purple/20 flex items-center gap-2 text-sm">
								<Edit3 size={14} />
								Edit Profile
							</button>
						)}
					</div>
				</div>

				{/* ========================= Tabs ========================= */}
				<div className="mb-6">
					<div className="flex border-b border-purple/20">
						{[
							{ key: "collections", label: "Collections", count: totalCards },
							{ key: "wishlist", label: "Wishlist", count: wishlist.length },
						].map(({ key, label, count }) => (
							<button key={key} onClick={() => setActiveTab(key)}
								className={`px-5 py-3 font-medium text-sm transition-all duration-300 relative flex items-center gap-2 ${
									activeTab === key
										? "text-purple border-b-2 border-purple"
										: "text-cream/50 hover:text-cream"
								}`}>
								{label}
								<span className={`text-xs px-1.5 py-0.5 rounded-full ${
									activeTab === key ? "bg-purple/20 text-purple" : "bg-cream/5 text-cream/40"
								}`}>
									{count}
								</span>
							</button>
						))}
					</div>
				</div>

				{/* ========================= Collections Tab ========================= */}
				{activeTab === "collections" && (
					<div className="space-y-6">
						{/* Create collection button */}
						{isOwnProfile && (
							<div>
								{!showCreateCollection ? (
									<button onClick={() => setShowCreateCollection(true)}
										className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-purple/20
											rounded-lg text-cream/50 hover:border-purple/50 hover:text-purple transition-all text-sm">
										<Plus className="w-4 h-4" />
										New Collection
									</button>
								) : (
									<div className="bg-[#12121a] border border-purple/20 rounded-lg p-4 max-w-md">
										<h3 className="font-medium text-cream text-sm mb-3">Create Collection</h3>
										<div className="space-y-3">
											<input type="text" placeholder="Collection name" value={newColName}
												onChange={(e) => setNewColName(e.target.value)}
												autoFocus
												className="w-full px-3 py-2 bg-dark border border-purple/20 rounded-lg text-cream text-sm
													placeholder-cream/30 focus:outline-none focus:ring-2 focus:ring-purple/50" />
											<textarea placeholder="Description (optional)" rows={2} value={newColDesc}
												onChange={(e) => setNewColDesc(e.target.value)}
												className="w-full px-3 py-2 bg-dark border border-purple/20 rounded-lg text-cream text-sm
													placeholder-cream/30 resize-none focus:outline-none focus:ring-2 focus:ring-purple/50" />
											<label className="flex items-center gap-2 text-sm text-cream/60 cursor-pointer">
												<input type="checkbox" checked={newColPublic} onChange={(e) => setNewColPublic(e.target.checked)}
													className="w-4 h-4 rounded border-purple/20 bg-dark text-purple focus:ring-purple/50" />
												Make public
											</label>
											<div className="flex gap-2">
												<button onClick={handleCreateCollection}
													disabled={!newColName.trim() || creatingCollection}
													className="px-4 py-2 bg-purple hover:bg-purple/90 disabled:bg-gray-700 disabled:text-cream/30
														text-white rounded-lg text-sm font-medium transition-colors">
													{creatingCollection ? "Creating…" : "Create"}
												</button>
												<button onClick={() => { setShowCreateCollection(false); setNewColName(""); setNewColDesc("") }}
													className="px-4 py-2 text-cream/50 hover:text-cream hover:bg-white/5 rounded-lg text-sm transition-colors">
													Cancel
												</button>
											</div>
										</div>
									</div>
								)}
							</div>
						)}

						{/* Collection sections */}
						{collections.length === 0 ? (
							<EmptyState type="collections" />
						) : (
							collections.map((collection) => {
								const isCollapsed = collapsedCollections.has(collection.id)
								const isEditing = editingCollectionId === collection.id
								const colValue = (collection.items || []).reduce(
									(s, it) => s + (it.purchase_price || 0) * (it.quantity || 1), 0
								)

								return (
									<div key={collection.id} className="bg-[#0e0e18] rounded-xl border border-purple/10 overflow-hidden">
										{/* Collection Header */}
										<div className="p-4 flex items-start gap-3">
											{/* Collapse toggle */}
											<button onClick={() => toggleCollapseCollection(collection.id)}
												className="mt-1 text-cream/40 hover:text-cream transition-colors shrink-0">
												{isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
											</button>

											<div className="flex-1 min-w-0">
												{isEditing ? (
													/* Inline edit form */
													<div className="space-y-2">
														<input type="text" value={editColName}
															onChange={(e) => setEditColName(e.target.value)}
															className="w-full px-2 py-1 bg-dark border border-purple/30 rounded text-cream text-sm
																focus:outline-none focus:ring-1 focus:ring-purple/50" />
														<input type="text" value={editColDesc} placeholder="Description"
															onChange={(e) => setEditColDesc(e.target.value)}
															className="w-full px-2 py-1 bg-dark border border-purple/20 rounded text-cream/70 text-xs
																focus:outline-none focus:ring-1 focus:ring-purple/50" />
														<label className="flex items-center gap-2 text-xs text-cream/50 cursor-pointer">
															<input type="checkbox" checked={editColPublic}
																onChange={(e) => setEditColPublic(e.target.checked)}
																className="w-3.5 h-3.5 rounded border-purple/20 bg-dark text-purple" />
															Public
														</label>
														<div className="flex gap-2">
															<button onClick={() => saveEditCollection(collection.id)}
																disabled={savingCollection || !editColName.trim()}
																className="flex items-center gap-1 px-2.5 py-1 bg-purple/90 hover:bg-purple text-white rounded text-xs font-medium transition-colors
																	disabled:opacity-50">
																<Check className="w-3 h-3" />
																{savingCollection ? "Saving…" : "Save"}
															</button>
															<button onClick={() => setEditingCollectionId(null)}
																className="flex items-center gap-1 px-2.5 py-1 text-cream/50 hover:text-cream hover:bg-white/5 rounded text-xs transition-colors">
																<X className="w-3 h-3" />
																Cancel
															</button>
														</div>
													</div>
												) : (
													/* Display mode */
													<>
														<div className="flex items-center gap-2 mb-0.5">
															<h3 className="text-lg font-semibold text-cream truncate">{collection.name}</h3>
															{collection.is_default && (
																<Star className="w-3.5 h-3.5 text-yellow-500 shrink-0" fill="currentColor" />
															)}
															{collection.is_public ? (
																<Globe className="w-3.5 h-3.5 text-green-500/60 shrink-0" />
															) : (
																<Lock className="w-3.5 h-3.5 text-cream/30 shrink-0" />
															)}
														</div>
														{collection.description && (
															<p className="text-xs text-cream/40 mb-1">{collection.description}</p>
														)}
														<div className="flex items-center gap-3 text-xs text-cream/50">
															<span>{collection.items?.length || 0} cards</span>
															{colValue > 0 && (
																<span className="text-green-400/80">
																	<PriceDisplay amountCad={colValue} />
																</span>
															)}
														</div>
													</>
												)}
											</div>

											{/* Action buttons */}
											{isOwnProfile && !isEditing && (
												<div className="flex items-center gap-1 shrink-0">
													<button onClick={() => startEditCollection(collection)}
														className="p-1.5 text-cream/30 hover:text-cream hover:bg-white/5 rounded-md transition-colors"
														title="Edit collection">
														<Edit3 className="w-3.5 h-3.5" />
													</button>
													{!collection.is_default && (
														<button onClick={() => handleDeleteCollection(collection.id)}
															className="p-1.5 text-cream/30 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
															title="Delete collection">
															<Trash2 className="w-3.5 h-3.5" />
														</button>
													)}
												</div>
											)}
										</div>

										{/* Card Grid */}
										{!isCollapsed && (
											<div className="px-4 pb-4">
												{(collection.items?.length || 0) === 0 ? (
													<div className="text-center py-8 text-cream/30 text-sm">
														No cards in this collection yet
													</div>
												) : (
													<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
														{collection.items.map((item) => (
															<CardTile
																key={item.card_id}
																item={item}
																type="collection"
																collectionId={collection.id}
															/>
														))}
													</div>
												)}
											</div>
										)}
									</div>
								)
							})
						)}
					</div>
				)}

				{/* ========================= Wishlist Tab ========================= */}
				{activeTab === "wishlist" && (
					<div>
						{wishlist.length === 0 ? (
							<EmptyState type="wishlist" />
						) : (
							<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
								{wishlist.map((item) => (
									<CardTile
										key={item.card_id}
										item={item}
										type="wishlist"
									/>
								))}
							</div>
						)}
					</div>
				)}
			</div>

			{/* ========================= Edit Item Modal ========================= */}
			<EditItemModal
				isOpen={!!editingItem}
				onClose={() => setEditingItem(null)}
				onSave={handleSaveItem}
				onDelete={handleDeleteItem}
				item={editingItem ? { ...editingItem, card_data: editingItem.card_data || cardDataCache.get(editingItem.card_id) || null } : null}
				mode={editingItemMode}
			/>

			{/* Collection selector for moving wishlist items */}
			<CollectionSelectorModal
				isOpen={!!moveCardId}
				onClose={() => setMoveCardId(null)}
				onSelectCollection={handleMoveCollectionSelected}
				cardId={moveCardId}
				cardName="this card"
			/>
		</div>
	)
}

export default ProfilePage
